import { ArrowUpRight, Eye, FileText, Hand, Menu, MessageSquarePlus, MousePointer2, MousePointerClick, PlusSquare, Undo2 } from "lucide-react";
import type { Tone } from "../lib/actionCopy";
import type { PublishControl, PublishStatusLine } from "../lib/publishControl";
import type { WorkspaceMode } from "../hooks/useWorkspaceMode";
import { BrandMark } from "./BrandMark";
import { Spinner } from "./Spinner";

export function ModeSwitch({ mode, onModeChange }: { mode: WorkspaceMode; onModeChange: (mode: WorkspaceMode) => void }) {
  return (
    <div className="bw-mode-switch" role="radiogroup" aria-label="Editor mode" onKeyDown={(event) => {
      if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return;
      event.preventDefault();
      const next = mode === "simple" ? "advanced" : "simple";
      onModeChange(next);
      event.currentTarget.querySelector<HTMLButtonElement>(`[data-mode="${next}"]`)?.focus();
    }}>
      {(["simple", "advanced"] as const).map((option) => (
        <button key={option} type="button" role="radio" data-mode={option} aria-label={option === "simple" ? "Simple" : "Advanced"} aria-checked={mode === option} tabIndex={mode === option ? 0 : -1}
          onClick={() => onModeChange(option)}>
          {option === "simple" ? "Simple" : "Advanced"}
        </button>
      ))}
    </div>
  );
}

/**
 * What a tap on the page does, with both choices always in view so it's clear which is on: browse the
 * site (the hand), or point at something to change it (the pointer).
 */
function TapSwitch({ pointing, onChange }: { pointing: boolean; onChange: (pointing: boolean) => void }) {
  const options = [
    { pointing: false, label: "Browse", hint: "Browse: taps follow links", Icon: Hand },
    { pointing: true, label: "Point to change", hint: "Point to change: tap anything to change it", Icon: MousePointerClick },
  ] as const;
  return (
    <div className="bw-tap-switch" role="radiogroup" aria-label="What a tap on the page does" onKeyDown={(event) => {
      if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return;
      event.preventDefault();
      onChange(!pointing);
      event.currentTarget.querySelector<HTMLButtonElement>(`[data-pointing="${!pointing}"]`)?.focus();
    }}>
      {options.map(({ pointing: value, label, hint, Icon }) => (
        <button key={label} type="button" role="radio" data-pointing={value} aria-checked={pointing === value} aria-label={label} title={hint} tabIndex={pointing === value ? 0 : -1} onClick={() => onChange(value)}>
          <Icon size={18} aria-hidden="true" />
        </button>
      ))}
    </div>
  );
}

interface WorkspaceBarProps {
  /** The website's name, or its address until it has one. */
  name: string;
  address: string;
  status: { label: string; tone: Tone };
  /** Where the site stands, under its name. */
  line: PublishStatusLine;
  publish: PublishControl;
  /** Opens or closes the menu drawer. */
  onMenu: () => void;
  menuOpen: boolean;
  mode: WorkspaceMode;
  /** Shows the Simple / Advanced switch. */
  onModeChange?: (mode: WorkspaceMode) => void;
  /** The live site, once it has been published; the address links to it. */
  liveHref?: string;
  undo?: { available: boolean; onUndo: () => void };
  /** Select a section in Advanced mode. */
  select?: { active: boolean; onToggle: () => void };
  /** Comment on the page, the other way to point at what should change; never on at the same time as editing. */
  comment?: { active: boolean; draftCount: number; onToggle: () => void };
  /** Opens the site as visitors see it, at desktop, tablet and phone sizes (wide screens; phones use the menu). */
  onPreview?: () => void;
  /** The Blog area has no Website publish: the bar names the site and links to the live blog instead. */
  shopArea?: { href: string | undefined };
  blogArea?: { href: string | undefined };
}

export function WorkspaceBar({ name, address, status, line, publish, onMenu, menuOpen, select, comment, mode, onModeChange, undo, liveHref, onPreview, blogArea, shopArea }: WorkspaceBarProps) {
  const moduleArea = blogArea ?? shopArea;
  return (
    <header className="bw-bar">
      <button
        type="button"
        className="bw-btn bw-btn--icon bw-menu-button"
        onClick={onMenu}
        aria-controls="bw-site-drawer"
        aria-expanded={menuOpen}
        aria-label={menuOpen ? "Hide the menu" : "Open the menu: pages, content, brand and history"}
      >
        <Menu size={22} aria-hidden="true" />
      </button>
      <div className="bw-identity">
        <BrandMark />
        <div className="bw-identity-copy">
          <strong>{name}</strong>
          <span className="bw-site-line">
            {!moduleArea && <span className="bw-site-dot" data-tone={line.tone} aria-hidden="true" />}
            {line.withAddress && name !== address && (liveHref ? (
              <a className="bw-site-address" href={liveHref} target="_blank" rel="noopener noreferrer" aria-label={`Open ${address}, the live site, in a new tab`}>
                {address}
                <ArrowUpRight size={13} aria-hidden="true" />
              </a>
            ) : <span className="bw-site-address">{address}</span>)}
            {!moduleArea && <span className="bw-site-state">{line.withAddress && name !== address ? `· ${line.text}` : line.text.charAt(0).toUpperCase() + line.text.slice(1)}</span>}
            {!moduleArea && <span className="bw-site-state-short">{line.short}</span>}
          </span>
        </div>
      </div>
      {onModeChange && <div className="bw-desktop-mode-switch"><ModeSwitch mode={mode} onModeChange={onModeChange} /></div>}
      {mode === "advanced" && !moduleArea && <div className="bw-toolbar" role="toolbar" aria-label="Page editing tools">
        <div className="bw-tool-pair">
          {select && <button type="button" className="bw-btn bw-btn--quiet" onClick={select.onToggle} aria-pressed={select.active}>
            <MousePointer2 size={18} aria-hidden="true" /><span className="bw-label-long">Select</span>
          </button>}
          {comment && <button type="button" className="bw-btn bw-btn--quiet" onClick={comment.onToggle} aria-pressed={comment.active}>
            <MessageSquarePlus size={18} aria-hidden="true" /><span className="bw-label-long">Comment</span>
          </button>}
        </div>
        {undo && <button type="button" className="bw-btn bw-btn--quiet bw-undo-button" disabled={!undo.available} onClick={undo.onUndo}>
          <Undo2 size={18} aria-hidden="true" /><span>Undo</span>
        </button>}
      </div>}
      {mode === "simple" && comment && !moduleArea && <TapSwitch pointing={comment.active} onChange={(pointing) => { if (pointing !== comment.active) comment.onToggle(); }} />}
      {moduleArea && moduleArea.href && <a className="bw-btn bw-btn--quiet bw-view-blog" href={moduleArea.href} target="_blank" rel="noopener noreferrer">{shopArea ? "View shop" : "View blog"}<ArrowUpRight size={16} aria-hidden="true" /></a>}
      {!moduleArea && onPreview && <button type="button" className="bw-btn bw-btn--icon bw-preview-open" onClick={onPreview} aria-label="Preview your site" title="Preview your site at desktop, tablet and phone sizes">
        <Eye size={20} aria-hidden="true" />
      </button>}
      {!moduleArea && <span className="bw-sr" role="status">{status.label}</span>}
      {!moduleArea && <button type="button" className="bw-btn bw-btn--primary bw-publish" disabled={publish.disabled}
        onClick={publish.onClick}
        aria-label={publish.label}
        title={status.label}
        data-tone={publish.tone}
      >
        {publish.busy ? <Spinner /> : publish.tone === "live" ? <span className="bw-site-dot" data-tone="live" aria-hidden="true" /> : null}
        <span className="bw-publish-label" aria-hidden="true">{publish.label}</span>
        <span className="bw-publish-short" aria-hidden="true">{publish.shortLabel ?? publish.label}</span>
      </button>}
    </header>
  );
}

interface MobileToolsProps {
  onPages: () => void;
  select?: { active: boolean; onToggle: () => void };
  comment?: { active: boolean; onToggle: () => void };
  onAdd: () => void;
  undo?: { available: boolean; onUndo: () => void };
}

/** The Advanced toolbar becomes five labelled, thumb-friendly actions below the preview. */
export function MobileTools({ onPages, select, comment, onAdd, undo }: MobileToolsProps) {
  return (
    <nav className="bw-mobile-tools" aria-label="Page editing tools">
      <button type="button" onClick={onPages}><FileText size={20} aria-hidden="true" /><span>Pages</span></button>
      <button type="button" aria-pressed={select?.active} onClick={select?.onToggle} disabled={!select}><MousePointer2 size={20} aria-hidden="true" /><span>Select</span></button>
      <button type="button" aria-pressed={comment?.active} onClick={comment?.onToggle} disabled={!comment}><MessageSquarePlus size={20} aria-hidden="true" /><span>Comment</span></button>
      <button type="button" onClick={onAdd}><PlusSquare size={20} aria-hidden="true" /><span>Add</span></button>
      <button type="button" onClick={undo?.onUndo} disabled={!undo?.available}><Undo2 size={20} aria-hidden="true" /><span>Undo</span></button>
    </nav>
  );
}
